import React from 'react'
import { t } from '@lingui/macro'

import { DownloadCSVButton } from '../../../components/common/download-csv-button/DownloadCSVButton'
import {
  ActivityHistoryDocument,
  ActivityHistoryQueryVariables
} from '../ActivityHistory.codegen'
import {
  formatDurationForCSV,
  getActivityFormattedName,
  getActivityRowLink,
  getActivityStatus,
  logActivityCSVDownload,
  getActivityParentInfo,
  checkIsTopic,
  checkIsCPDEvidence,
  formatActivityScore,
  getCpdTextStatus,
  getActivityFormattedDate
} from '../activityHistoryUtils'
import { ActivityHistoryRowType } from '../activityHistoryTypes'
import IconDownload from '../../../components/common/icon/IconDownload'
import {
  ACTIVITY_HISTORY_STATUSES,
  ACTIVITY_TYPE_NAMES
} from '../activityHistoryConstants'
import { ExtendedChakraButtonProps } from 'front-end/src/components'
import { useCPDFeatureFlag } from 'front-end/src/app/hooks/useFeatureFlags'

type ActivityHistoryDownloadCSVProps = Omit<
  ExtendedChakraButtonProps,
  'children'
> & {
  filters: ActivityHistoryQueryVariables
  count: number
  isCpdEnabled: boolean
}

export const ACTIVITY_HISTORY_STATUS_TRANSLATIONS = {
  [ACTIVITY_HISTORY_STATUSES.COMPLETED]: t`Completed`,
  [ACTIVITY_HISTORY_STATUSES.IN_PROGRESS]: t`In progress`,
  [ACTIVITY_HISTORY_STATUSES.FAILED]: t`Failed`,
  [ACTIVITY_HISTORY_STATUSES.ATTENDED]: t`Attended`,
  [ACTIVITY_HISTORY_STATUSES.OVERDUE]: t`In progress`
}

const prepareDataWithCpd = (data, isCpdEnabled: boolean, cpdAlias: string) =>
  data.map((row: ActivityHistoryRowType) => {
    const status = getActivityStatus(row)
    const statusTranslated = ACTIVITY_HISTORY_STATUS_TRANSLATIONS[status]
    // For easier check
    const rowForDate = row as any
    const activityType = ACTIVITY_TYPE_NAMES[row.__typename]

    const result = {
      [t`Activity name`]: getActivityFormattedName(row),
      [t`Link`]: getActivityRowLink(row),
      [t`Context`]: getActivityParentInfo(row),
      [t`Activity type`]: t`${activityType}`,
      [t`Shared on`]: checkIsTopic(row)
        ? getActivityFormattedDate(row?.share_batch_ts)
        : '',
      [t`Shared by`]: checkIsTopic(row) ? row.sharedBy?.display_name : '',
      [t`Upload on`]: checkIsCPDEvidence(row)
        ? getActivityFormattedDate(row.created_at)
        : '',
      [t`Started`]: getActivityFormattedDate(
        rowForDate.started_at || rowForDate?.event?.start_time
      ),
      [t`Completed`]: getActivityFormattedDate(
        rowForDate.completed_at || rowForDate?.event?.end_time
      ),
      [t`Due date`]: checkIsTopic(row)
        ? getActivityFormattedDate(row.target_completion_date)
        : '',
      [t`Last activity`]: checkIsTopic(row)
        ? getActivityFormattedDate(row.lastViewedAt)
        : '',
      [t`Version`]: checkIsTopic(row) ? row?.topicVersion?.version : '',
      [t`Status`]: statusTranslated || '',
      [t`Score`]: formatActivityScore(row),
      [t`Duration`]: formatDurationForCSV(row) || ''
    }

    return isCpdEnabled
      ? { ...result, [cpdAlias]: getCpdTextStatus(row) }
      : result
  })

const ActivityHistoryDownloadCSV = ({
  filters,
  count,
  isCpdEnabled,
  ...rest
}: ActivityHistoryDownloadCSVProps) => {
  const { cpdAlias } = useCPDFeatureFlag()

  return (
    <DownloadCSVButton
      count={count}
      fileName={'looop-activity-history'}
      dataPath={'data.me.activityHistory.items'}
      leftIcon={<IconDownload />}
      title={t`Download report (.csv)`}
      h="40px"
      ml="8px"
      queryOptions={(page, limit) => ({
        variables: {
          filters: {
            ...filters.filters,
            limit,
            page
          }
        },
        query: ActivityHistoryDocument
      })}
      prepareData={(data) => {
        return prepareDataWithCpd(data, isCpdEnabled, cpdAlias)
      }}
      onFetchEnd={logActivityCSVDownload}
      {...rest}
    />
  )
}

export default ActivityHistoryDownloadCSV
